import { MediaPreview } from "@/components/studio/file-upload";
import {
  AUDIO_ACCEPT_ATTR,
  THUMBNAIL_ACCEPT_ATTR,
} from "@/lib/thumbnail-validation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Coach } from "@/lib/schema";
import { Upload } from "lucide-react";

interface VariantData {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription?: string;
  audioFileUrl?: string | null;
  audioFileName?: string | null;
  thumbnailUrl?: string | null;
  durationSeconds?: number | null;
  coachId?: string | null;
  coachName?: string | null;
}

interface VariantErrors {
  localizedTitle?: { message?: string };
  audioFileUrl?: { message?: string };
  thumbnailUrl?: { message?: string };
}

interface AudioVariantFieldsProps {
  variant: VariantData;
  onFieldChange: (field: string, value: any) => void;
  onFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "audioFileUrl" | "thumbnailUrl",
  ) => void;
  coaches: Coach[];
  isUploading: boolean;
  isReadOnly?: boolean;
  errors?: VariantErrors;
  isCoachLocked?: boolean;
}

export function AudioVariantFields({
  variant,
  onFieldChange,
  onFileUpload,
  coaches,
  isUploading,
  isReadOnly,
  errors,
  isCoachLocked,
}: AudioVariantFieldsProps) {
  const lockedCoachName =
    isCoachLocked && variant.coachId
      ? (variant.coachName ??
        coaches.find((c) => c.id === variant.coachId)?.name ??
        "")
      : "";
  return (
    <div className="space-y-4 pt-4">
      <div className="space-y-2">
        <Label>Title *</Label>
        <Input
          value={variant.localizedTitle}
          onChange={(e) => onFieldChange("localizedTitle", e.target.value)}
          placeholder="Enter audio title"
          disabled={isReadOnly}
          data-testid="input-title"
          className={errors?.localizedTitle ? "border-destructive" : ""}
        />
        {errors?.localizedTitle && (
          <p className="text-sm text-destructive">
            {errors.localizedTitle.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={variant.localizedDescription || ""}
          onChange={(e) =>
            onFieldChange("localizedDescription", e.target.value)
          }
          placeholder="Describe this audio content"
          rows={3}
          maxLength={150}
          disabled={isReadOnly}
          data-testid="input-description"
        />
        <p className="text-xs text-muted-foreground text-right">
          {variant.localizedDescription?.length ?? 0}/150
        </p>
      </div>

      <div className="space-y-2">
        <Label>Audio File *</Label>
        {variant.audioFileUrl ? (
          <div className="space-y-2">
            <MediaPreview
              url={variant.audioFileUrl}
              mediaType="audio"
              onRemove={
                isReadOnly
                  ? undefined
                  : () => {
                      onFieldChange("audioFileUrl", "");
                      onFieldChange("audioFileName", null);
                    }
              }
              isReadOnly={isReadOnly}
            />
            {!isReadOnly && (
              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border cursor-pointer hover-elevate text-sm text-muted-foreground">
                <Upload className="h-3.5 w-3.5" />
                Replace Audio
                <input
                  type="file"
                  accept={AUDIO_ACCEPT_ATTR}
                  className="hidden"
                  onChange={(e) => onFileUpload(e, "audioFileUrl")}
                  disabled={isUploading || isReadOnly}
                  data-testid="input-audio-file-replace"
                />
              </label>
            )}
          </div>
        ) : (
          <label
            className={`flex items-center gap-2 px-4 py-2 rounded-md border cursor-pointer hover-elevate ${
              errors?.audioFileUrl ? "border-destructive" : "border-border"
            } ${isReadOnly ? "opacity-60 pointer-events-none" : ""}`}
          >
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {isUploading ? "Uploading..." : "Upload Audio"}
            </span>
            <input
              type="file"
              accept={AUDIO_ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => onFileUpload(e, "audioFileUrl")}
              disabled={isUploading || isReadOnly}
              data-testid="input-audio-file"
            />
          </label>
        )}
        {errors?.audioFileUrl && (
          <p className="text-sm text-destructive">
            {errors.audioFileUrl.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Thumbnail Image *</Label>
        {variant.thumbnailUrl ? (
          <div className="space-y-2">
            <MediaPreview
              url={variant.thumbnailUrl}
              mediaType="image"
              onRemove={
                isReadOnly ? undefined : () => onFieldChange("thumbnailUrl", "")
              }
              isReadOnly={isReadOnly}
            />
            {!isReadOnly && (
              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border cursor-pointer hover-elevate text-sm text-muted-foreground">
                <Upload className="h-3.5 w-3.5" />
                Replace
                <input
                  type="file"
                  accept={THUMBNAIL_ACCEPT_ATTR}
                  className="hidden"
                  onChange={(e) => onFileUpload(e, "thumbnailUrl")}
                  disabled={isUploading || isReadOnly}
                  data-testid="input-thumbnail-replace"
                />
              </label>
            )}
          </div>
        ) : (
          <label
            className={`flex items-center gap-2 px-4 py-2 rounded-md border cursor-pointer hover-elevate ${
              errors?.thumbnailUrl ? "border-destructive" : "border-border"
            } ${isReadOnly ? "opacity-60 pointer-events-none" : ""}`}
          >
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Upload Thumbnail</span>
            <input
              type="file"
              accept={THUMBNAIL_ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => onFileUpload(e, "thumbnailUrl")}
              disabled={isUploading || isReadOnly}
              data-testid="input-thumbnail"
            />
          </label>
        )}
        {errors?.thumbnailUrl && (
          <p className="text-sm text-destructive">
            {errors.thumbnailUrl.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Duration (seconds)</Label>
          <Input
            type="number"
            value={variant.durationSeconds ?? ""}
            onChange={(e) =>
              onFieldChange(
                "durationSeconds",
                e.target.value ? Number(e.target.value) : null,
              )
            }
            placeholder="Auto-detected on upload"
            min={0}
            disabled={isReadOnly}
            data-testid="input-duration"
          />
        </div>

        <div className="space-y-2">
          <Label>Coach Attribution</Label>
          {isCoachLocked ? (
            <Input
              value={lockedCoachName}
              disabled
              data-testid="select-coach"
            />
          ) : (
            <Select
              value={variant.coachId || "none"}
              onValueChange={(val) =>
                onFieldChange("coachId", val === "none" ? null : val)
              }
              disabled={isReadOnly}
            >
              <SelectTrigger data-testid="select-coach">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  );
}
